import React, { useState, useEffect, useMemo } from 'react';
import { ThumbsUp, ShieldAlert } from 'lucide-react';
import ChartRadial from '@/components/charts/RadialNew';
import { extractNumber } from '@/lib/utils';
import { TotalData } from '../../index.type';
interface HoldingsStructureProps {
  selectedMetrics: {
    holdingsStructure?: boolean;
  };
  totalData: Pick<TotalData, 'cash_before' | 'stable_before' | 'aggressive_before'>;
}

const HoldingsStructure: React.FC<HoldingsStructureProps> = ({ selectedMetrics, totalData }) => {
  const [currentSelectedMetrics, setCurrentSelectedMetrics] = useState<{
    holdingsStructure?: boolean;
  }>({
    holdingsStructure: false
  });

  useEffect(() => {
    setCurrentSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  const handleCheckboxChange = (checked: boolean) => {
    setCurrentSelectedMetrics(() => ({
      holdingsStructure: checked
    }));
  };

  const chartData = useMemo(() => {
    return [
      {
        name: '现金管理',
        value: Number(extractNumber(totalData.cash_before || '')) || 0,
        color: '#5959EB'
      },
      {
        name: '稳健收益',
        value: Number(extractNumber(totalData.stable_before || '')) || 0,
        color: '#FF7B52'
      },
      {
        name: '进取收益',
        value: Number(extractNumber(totalData.aggressive_before || '')) || 0,
        color: '#F8467B'
      }
    ];
  }, [totalData]);

  return (
    <div className="rounded-lg border border-[#F0F0F0] p-3">
      {/* 标题和复选框 */}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={currentSelectedMetrics.holdingsStructure || false}
          onChange={e => handleCheckboxChange(e.target.checked)}
          className="h-4 w-4"
        />
        <span className="text-base font-semibold text-[#1F1F1F]">持仓结构</span>
      </div>

      {/* 图表和图例 */}
      <div className="flex items-center justify-center gap-12">
        <div className="h-[150px] w-full">
          <ChartRadial
            data={chartData}
            innerRadius={40}
            outerRadius={60}
            cx="50%"
            cy="40%"
            legendClassName="absolute left-1/2 transform translate-x-1/2 -translate-y-full mt-4 ml-[10px] top-1/2 gap-2"
          />
        </div>
      </div>

      <div className="space-y-3">
        {/* 可继续持有的产品 */}
        <div className="overflow-hidden rounded-lg border border-[#F0F0F0]">
          {/* 标题栏 */}
          <div className="flex items-center gap-2 bg-gradient-to-r from-[#EEEDFF] to-[#F6F7FF] px-3 py-2">
            <div className="flex h-4 w-4 items-center justify-center">
              <ThumbsUp />
            </div>
            <span className="text-sm font-semibold text-[#1F1F1F]">可继续持有的产品</span>
          </div>

          {/* 产品内容 */}
          <div className="bg-gradient-to-r from-white to-[#F8F7FF] p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-base font-semibold text-[#1F1F1F]">
                易方达蓝筹精选混合(005827)
              </span>
              <span className="text-base font-semibold text-[#F5222D]">+18.24%</span>
            </div>
            <p className="text-xs leading-relaxed text-[#1F1F1F]">
              近3年收益率优于同类平均水平15.2%；
              <br />
              基金经理投资风格稳健，选股能力突出；
              <br />
              波动率控制良好，夏普比率1.85处于行业前20%。
            </p>
          </div>
        </div>

        {/* 需调整优化的产品 */}
        <div className="overflow-hidden rounded-lg border border-[#F0F0F0]">
          {/* 标题栏 */}
          <div className="flex items-center gap-2 bg-gradient-to-r from-[#F1FFF8] to-[#F5FFFA] px-3 py-2">
            <div className="flex h-4 w-4 items-center justify-center">
              <ShieldAlert />
            </div>
            <span className="text-sm font-semibold text-[#1F1F1F]">需调整优化的产品</span>
          </div>

          {/* 产品内容 */}
          <div className="bg-gradient-to-r from-white to-[#F5FFFA] p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-base font-semibold text-[#1F1F1F]">
                华夏新能源龙头混合A(011329)
              </span>
              <span className="text-base font-semibold text-[#059669]">-12.5%</span>
            </div>
            <p className="text-xs leading-relaxed text-[#1F1F1F]">
              近6个月表现不佳，跑输同类平均8.6%；
              <br />
              行业集中度过高，组合中已有同类产品重叠；
              <br />
              建议减持并分散至其他行业基金。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoldingsStructure;
